import React, { useState, useEffect, useCallback } from 'react';
import {
  meterReadingsApi,
  MeterReadingType,
  ApartmentMeterData,
  MeterReadingActionLog,
} from '../../api/meterReadingsApi';
import { BillingPeriod } from '../../api/feeCategoriesApi';
import { ImageModal } from '../ImageModal';
import { MeterReadingImportModal } from './MeterReadingImportModal';
import { BulkListPagination } from './BulkListPagination';
import { useAuth } from '../../contexts/AuthContext';
import { useFunctionPermissions } from '../../hooks/useFunctionPermissions';
import { getApiErrorMessage } from '../../utils/apiErrors';

function isReadingReadyToSubmit(row: ApartmentMeterData): boolean {
  const newReading = row.newReading;
  if (newReading === undefined || newReading === null || Number.isNaN(newReading)) {
    return false;
  }
  const oldReading = row.oldReading ?? 0;
  return newReading > oldReading;
}

function isBaselineOldReadingRow(row: ApartmentMeterData): boolean {
  if (isReadingReadyToSubmit(row)) return false;
  const oldReading = row.oldReading;
  if (oldReading === undefined || oldReading === null || Number.isNaN(oldReading)) {
    return false;
  }
  const newReading = row.newReading;
  if (newReading !== undefined && newReading !== null && !Number.isNaN(newReading)) {
    return false;
  }
  return true;
}

interface MeterReadingsPanelProps {
  buildingId: string;
  buildingName: string;
  type: MeterReadingType;
  billingPeriods: BillingPeriod[];
  selectedPeriod: BillingPeriod | null;
  onPeriodChange: (period: BillingPeriod | null) => void;
  t: (key: string) => string;
}

const formatNumberDisplay = (value: number | string) => {
  if (!value && value !== 0) return '';
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/\./g, '')) : value;
  if (isNaN(numValue)) return '';
  return new Intl.NumberFormat('vi-VN').format(numValue);
};

const parseNumberInput = (formattedValue: string) => formattedValue.replace(/\./g, '');

const computeUsageVariancePercent = (
  currentUsage?: number,
  previousUsage?: number,
): number | null => {
  if (currentUsage == null || previousUsage == null || previousUsage <= 0) {
    return null;
  }
  return ((currentUsage - previousUsage) / previousUsage) * 100;
};

const formatVarianceDisplay = (percent: number | null): string => {
  if (percent == null || Number.isNaN(percent)) return '-';
  const rounded = Math.round(percent * 10) / 10;
  if (rounded === 0) return '0.0%';
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toFixed(1)}%`;
};

const isVarianceAlert = (percent: number | null): boolean =>
  percent != null && (percent > 30 || percent < -30);

type PanelView = 'list' | 'entry' | 'history';

export const MeterReadingsPanel: React.FC<MeterReadingsPanelProps> = ({
  buildingId,
  buildingName,
  type,
  billingPeriods,
  selectedPeriod,
  onPeriodChange,
  t,
}) => {
  const { isOperator, isSuperAdmin } = useAuth();
  const { can } = useFunctionPermissions();
  const canImportReadings = can('METER_READING_IMPORT', 'import');
  const canEditOldReading = isOperator || isSuperAdmin;

  const [readings, setReadings] = useState<ApartmentMeterData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [listSearchTerm, setListSearchTerm] = useState('');
  const [debouncedListSearch, setDebouncedListSearch] = useState('');
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [listTotalPages, setListTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasImportedData, setHasImportedData] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [entryTotal, setEntryTotal] = useState(0);
  const [entryTotalPages, setEntryTotalPages] = useState(1);
  const [pageSize] = useState(25);
  const [pendingEdits, setPendingEdits] = useState<Map<string, ApartmentMeterData>>(new Map());
  const [imageModal, setImageModal] = useState({ isOpen: false, imageUrl: '', fileName: '' });
  const [showImportModal, setShowImportModal] = useState(false);
  const [panelView, setPanelView] = useState<PanelView>('list');
  const [actionLogs, setActionLogs] = useState<MeterReadingActionLog[]>([]);
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(25);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPeriodFilter, setHistoryPeriodFilter] = useState<'selected' | 'all'>('selected');

  const periodString = selectedPeriod
    ? `${selectedPeriod.month.toString().padStart(2, '0')}/${selectedPeriod.year}`
    : '';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedListSearch(listSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [listSearchTerm]);

  const loadEntryReadings = useCallback(async () => {
    if (!buildingId || !selectedPeriod) return;
    try {
      setLoading(true);
      const periodStr = `${selectedPeriod.month.toString().padStart(2, '0')}/${selectedPeriod.year}`;
      const result = await meterReadingsApi.getMetersWithMeterData(buildingId, type, periodStr, {
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch || undefined,
      });
      setPendingEdits((currentEdits) => {
        setReadings(
          result.data.map((row) => {
            const edit = row.utilityMeterId ? currentEdits.get(row.utilityMeterId) : undefined;
            return edit ? { ...row, ...edit } : row;
          }),
        );
        return currentEdits;
      });
      setEntryTotal(result.meta.total);
      setEntryTotalPages(result.meta.totalPages);
    } catch {
      setError(t('failedToLoadApartments'));
    } finally {
      setLoading(false);
    }
  }, [
    buildingId,
    selectedPeriod,
    type,
    currentPage,
    pageSize,
    debouncedSearch,
    t,
  ]);

  const loadListReadings = useCallback(async () => {
    if (!buildingId || !selectedPeriod) return;
    try {
      setLoading(true);
      const periodStr = `${selectedPeriod.month.toString().padStart(2, '0')}/${selectedPeriod.year}`;
      const result = await meterReadingsApi.getMetersWithMeterData(buildingId, type, periodStr, {
        page: listPage,
        limit: pageSize,
        search: debouncedListSearch || undefined,
        recordedOnly: true,
      });
      setReadings(result.data);
      setListTotal(result.meta.total);
      setListTotalPages(result.meta.totalPages);
    } catch {
      setError(t('failedToLoadApartments'));
    } finally {
      setLoading(false);
    }
  }, [
    buildingId,
    selectedPeriod,
    type,
    listPage,
    pageSize,
    debouncedListSearch,
    t,
  ]);

  const loadActionLogs = useCallback(async () => {
    if (!buildingId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await meterReadingsApi.getActionLogs({
        buildingId,
        type,
        period:
          historyPeriodFilter === 'selected' && selectedPeriod ? periodString : undefined,
        search: historySearch || undefined,
        page: historyPage,
        limit: historyPageSize,
      });
      setActionLogs(result.data);
      setHistoryTotalPages(result.meta.totalPages);
      setHistoryTotal(result.meta.total);
    } catch {
      setError(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [
    buildingId,
    type,
    historyPeriodFilter,
    selectedPeriod,
    periodString,
    historySearch,
    historyPage,
    historyPageSize,
    t,
  ]);

  useEffect(() => {
    setHasImportedData(false);
    setReadings([]);
    setPendingEdits(new Map());
    setHistoryPage(1);
    setListPage(1);
    setCurrentPage(1);
  }, [buildingId, type]);

  useEffect(() => {
    if (!buildingId || !selectedPeriod) return;
    if (panelView === 'list') {
      loadListReadings();
    }
  }, [panelView, buildingId, selectedPeriod, loadListReadings]);

  useEffect(() => {
    if (!buildingId || !selectedPeriod) return;
    if (panelView === 'entry' && showManualEntry && !hasImportedData) {
      loadEntryReadings();
    }
  }, [
    panelView,
    showManualEntry,
    hasImportedData,
    buildingId,
    selectedPeriod,
    loadEntryReadings,
  ]);

  useEffect(() => {
    if (panelView === 'history' && buildingId) {
      loadActionLogs();
    }
  }, [panelView, buildingId, loadActionLogs]);

  const formatActionLabel = (action: MeterReadingActionLog['action']) => {
    switch (action) {
      case 'CREATE':
        return t('cardActionCreate');
      case 'UPDATE':
        return t('cardActionUpdate');
      case 'DELETE':
        return t('cardActionDelete');
      default:
        return action;
    }
  };

  const getActionBadgeClass = (action: MeterReadingActionLog['action']) => {
    switch (action) {
      case 'CREATE':
        return 'bg-green-100 text-green-800';
      case 'UPDATE':
        return 'bg-blue-100 text-blue-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatReadingValue = (value?: number | null) => {
    if (value === null || value === undefined) return '--';
    return formatNumberDisplay(value);
  };

  const formatReadingChange = (
    log: MeterReadingActionLog,
    field: 'oldReading' | 'newReading',
  ) => {
    const current = log[field];
    const before = log.metadata?.before?.[field];
    if (log.action === 'UPDATE' && before != null && before !== current) {
      return `${formatReadingValue(before)} → ${formatReadingValue(current)}`;
    }
    return formatReadingValue(current);
  };

  const handleReadingChange = (utilityMeterId: string, value: string) => {
    const numValue = parseFloat(parseNumberInput(value)) || 0;
    setReadings((prev) => {
      const updatedRows = prev.map((r) => {
        if (r.utilityMeterId !== utilityMeterId) return r;
        const updated = { ...r, newReading: numValue };
        updated.usage = Math.max(0, numValue - (updated.oldReading || 0));
        return updated;
      });
      const updatedRow = updatedRows.find((r) => r.utilityMeterId === utilityMeterId);
      if (updatedRow) {
        setPendingEdits((p) => {
          const next = new Map(p);
          next.set(utilityMeterId, updatedRow);
          return next;
        });
      }
      return updatedRows;
    });
  };

  const handleOldReadingChange = (utilityMeterId: string, value: string) => {
    const numValue = parseFloat(parseNumberInput(value)) || 0;
    setReadings((prev) => {
      const updatedRows = prev.map((r) => {
        if (r.utilityMeterId !== utilityMeterId) return r;
        const updated = { ...r, oldReading: numValue };
        if (updated.newReading !== undefined) {
          updated.usage = Math.max(0, (updated.newReading || 0) - numValue);
        }
        return updated;
      });
      const updatedRow = updatedRows.find((r) => r.utilityMeterId === utilityMeterId);
      if (updatedRow) {
        setPendingEdits((p) => {
          const next = new Map(p);
          next.set(utilityMeterId, updatedRow);
          return next;
        });
      }
      return updatedRows;
    });
  };

  const handleExport = async () => {
    if (!buildingId || !selectedPeriod) return;
    try {
      await meterReadingsApi.exportReadings(buildingId, type, periodString);
    } catch {
      setError(t('failedToExportSample'));
    }
  };

  const handleDeletePeriodReadings = async () => {
    if (!buildingId || !selectedPeriod) return;
    const label = periodString;
    const confirmed = window.confirm(
      t('confirmDeletePeriodReadings').replace('{period}', label),
    );
    if (!confirmed) return;

    try {
      setLoading(true);
      setError(null);
      const result = await meterReadingsApi.deleteReadingsByPeriod(buildingId, type, periodString);
      alert(result.message);
      setHasImportedData(false);
      setPendingEdits(new Map());
      if (panelView === 'list') {
        loadListReadings();
      } else if (panelView === 'entry') {
        loadEntryReadings();
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t('failedToDeletePeriodReadings')));
    } finally {
      setLoading(false);
    }
  };

  const handleImportedReadings = (importedData: ApartmentMeterData[]) => {
    if (importedData.length === 0) {
      setError(t('meterImportNoRows'));
      return;
    }
    const endingCount = importedData.filter(
      (row) =>
        row.newReading !== undefined &&
        row.newReading !== null &&
        !Number.isNaN(row.newReading),
    ).length;
    const edits = new Map<string, ApartmentMeterData>();
    importedData.forEach((item) => {
      if (item.utilityMeterId) {
        edits.set(item.utilityMeterId, item);
      }
    });
    setPendingEdits(edits);
    setReadings(importedData);
    setEntryTotal(importedData.length);
    setEntryTotalPages(1);
    setCurrentPage(1);
    setHasImportedData(true);
    setShowManualEntry(true);
    if (endingCount === 0) {
      setError(t('meterSubmitNoEndingReadings'));
      return;
    }
    setError(null);
    alert(t('importReadingsSuccess').replace('{count}', String(importedData.length)));
  };

  const BULK_SUBMIT_BATCH_SIZE = 75;

  const handleSubmit = async () => {
    if (!buildingId || !selectedPeriod) return;
    const toSubmitSource =
      pendingEdits.size > 0
        ? Array.from(pendingEdits.values())
        : readings;
    const readyRows = toSubmitSource.filter(isReadingReadyToSubmit);
    const baselineRows = toSubmitSource.filter(isBaselineOldReadingRow);
    const toSubmit = [...readyRows, ...baselineRows];
    if (toSubmit.length === 0) {
      setError(t('pleaseEnterValidReadings'));
      return;
    }
    if (readyRows.length === 0 && baselineRows.length > 0) {
      setError(t('meterSubmitNoEndingReadings'));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const periodString = `${selectedPeriod.month.toString().padStart(2, '0')}/${selectedPeriod.year}`;
      const payloadReadings = toSubmit.map((r) => ({
        apartmentId: r.id,
        utilityMeterId: r.utilityMeterId,
        oldReading: (r.oldReading || 0).toString(),
        newReading:
          r.newReading !== undefined && r.newReading !== null
            ? String(r.newReading)
            : '',
        imagePath: r.imagePath,
      }));

      let createdCount = 0;
      let updatedCount = 0;
      let baselineUpdatedCount = 0;
      for (let i = 0; i < payloadReadings.length; i += BULK_SUBMIT_BATCH_SIZE) {
        const batch = payloadReadings.slice(i, i + BULK_SUBMIT_BATCH_SIZE);
        const result = await meterReadingsApi.submitBulkReadings({
          buildingId,
          type,
          period: periodString,
          readings: batch,
        });
        createdCount += result.createdCount;
        updatedCount += result.updatedCount;
        baselineUpdatedCount += result.baselineUpdatedCount ?? 0;
      }

      const baselineLine =
        baselineUpdatedCount > 0
          ? `\n${t('meterBaselineSavedCount').replace('{count}', String(baselineUpdatedCount))}`
          : '';
      alert(
        `${t('bulkFeeEntrySuccess')}\nCreated: ${createdCount}, Updated: ${updatedCount}${baselineLine}`,
      );
      setHasImportedData(false);
      setPendingEdits(new Map());
      if (panelView === 'list') {
        loadListReadings();
      } else if (panelView === 'entry') {
        loadEntryReadings();
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, t('failedToSubmitBulkEntry')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('billingPeriod')} <span className="text-red-500">*</span>
        </label>
        <select
          value={selectedPeriod ? `${selectedPeriod.month}-${selectedPeriod.year}` : ''}
          onChange={(e) => {
            if (!e.target.value) {
              onPeriodChange(null);
              return;
            }
            const [month, year] = e.target.value.split('-');
            const period = billingPeriods.find(
              (p) => p.month === parseInt(month, 10) && p.year === parseInt(year, 10),
            );
            onPeriodChange(period || null);
          }}
          className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{t('selectBillingPeriod')}</option>
          {billingPeriods.map((period) => (
            <option key={`${period.month}-${period.year}`} value={`${period.month}-${period.year}`}>
              {period.label}
            </option>
          ))}
        </select>
        {selectedPeriod && (panelView === 'entry' || panelView === 'list') && (isOperator || isSuperAdmin) && (
          <button
            type="button"
            onClick={handleDeletePeriodReadings}
            disabled={loading}
            className="mt-3 text-sm text-red-600 hover:text-red-800 underline disabled:opacity-50"
          >
            {t('deletePeriodReadings')}
          </button>
        )}
      </div>

      <div className="flex border-b border-gray-200 mb-6">
        <button
          type="button"
          onClick={() => {
            setPanelView('list');
            setListPage(1);
          }}
          className={`px-6 py-3 font-medium border-b-2 -mb-px ${
            panelView === 'list'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('meterReadingListTab')}
        </button>
        <button
          type="button"
          onClick={() => setPanelView('entry')}
          className={`px-6 py-3 font-medium border-b-2 -mb-px ${
            panelView === 'entry'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('meterReadingEntryTab')}
        </button>
        <button
          type="button"
          onClick={() => {
            setPanelView('history');
            setHistoryPage(1);
          }}
          className={`px-6 py-3 font-medium border-b-2 -mb-px ${
            panelView === 'history'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('meterReadingHistoryTab')}
        </button>
      </div>

      {panelView === 'list' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b flex justify-between items-center gap-4">
            <h3 className="font-medium text-gray-800">{t('meterReadingListTab')}</h3>
            <input
              type="text"
              value={listSearchTerm}
              onChange={(e) => {
                setListSearchTerm(e.target.value);
                setListPage(1);
              }}
              placeholder={t('searchApartmentOrMeter')}
              className="border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="p-4 overflow-x-auto">
            {!selectedPeriod ? (
              <div className="text-center py-8 text-gray-500">{t('selectBillingPeriod')}</div>
            ) : loading ? (
              <div className="text-center py-12 text-gray-500">{t('loading')}</div>
            ) : listTotal === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {t('noRecordedReadingsForPeriod').replace('{period}', periodString)}
              </div>
            ) : readings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">{t('noData')}</div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('apartmentCode')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('meterCode')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oldReading')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('newReading')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('usage')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('usageVariance')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('image')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {readings.map((row, index) => (
                      <tr key={row.utilityMeterId ?? row.id}>
                        <td className="px-4 py-3 text-sm">{(listPage - 1) * pageSize + index + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-blue-600">{row.apartmentCode}</td>
                        <td className="px-4 py-3 text-sm">{row.meterCode ?? '-'}</td>
                        <td className="px-4 py-3 text-sm">{formatNumberDisplay(row.oldReading || 0)}</td>
                        <td className="px-4 py-3">
                          <input
                            readOnly
                            value={formatNumberDisplay(row.newReading || '')}
                            className="w-24 border rounded px-2 py-1 text-sm bg-gray-100"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {row.usage !== undefined ? formatNumberDisplay(row.usage) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {(() => {
                            const variance = computeUsageVariancePercent(
                              row.usage,
                              row.previousPeriodUsage,
                            );
                            return (
                              <span
                                className={
                                  isVarianceAlert(variance) ? 'text-red-600 font-medium' : 'text-gray-700'
                                }
                              >
                                {formatVarianceDisplay(variance)}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {row.imagePath ? (
                            <button
                              type="button"
                              onClick={() =>
                                setImageModal({
                                  isOpen: true,
                                  imageUrl: row.imagePath!,
                                  fileName: row.meterCode || row.apartmentCode,
                                })
                              }
                              className="text-blue-600 hover:text-blue-800"
                            >
                              {t('readingHasImage')}
                            </button>
                          ) : (
                            <span className="text-gray-400">{t('readingNoImage')}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                            {t('readingStatusRecorded')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <BulkListPagination
                  page={listPage}
                  totalPages={listTotalPages}
                  total={listTotal}
                  onPageChange={setListPage}
                  t={t}
                />
              </>
            )}
          </div>
        </div>
      )}

      {panelView === 'entry' && (
        <>
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          type="button"
          onClick={handleExport}
          disabled={!buildingId || !selectedPeriod}
          className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
        >
          📋 {t('exportReadings')}
        </button>
        {canImportReadings && (
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            disabled={!buildingId || !selectedPeriod}
            className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 px-4 py-2 rounded-lg disabled:opacity-50"
          >
            📥 {t('importReadings')}
          </button>
        )}
      </div>

      <div className="mb-6">
        <button
          type="button"
          onClick={() => {
            const next = !showManualEntry;
            setShowManualEntry(next);
            if (next) {
              setHasImportedData(false);
              setCurrentPage(1);
              loadEntryReadings();
            }
          }}
          disabled={!buildingId || !selectedPeriod}
          className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg disabled:opacity-50 font-medium"
        >
          📝 {showManualEntry ? t('hideManualEntry') : t('showManualEntry')}
        </button>
      </div>

      {showManualEntry && selectedPeriod && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b flex justify-between items-center gap-4">
            <h3 className="font-medium text-gray-800">{t('manualEntrySection')}</h3>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={t('searchApartmentOrMeter')}
              className="border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="p-4 overflow-x-auto">
            {hasImportedData && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-blue-700 text-xs">
                📥 {t('importedFromExcel')}
              </div>
            )}
            {loading && !hasImportedData ? (
              <div className="text-center py-8 text-gray-500">{t('loading')}</div>
            ) : readings.length > 0 ? (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('apartmentCode')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('meterCode')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('oldReading')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('newReading')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('usage')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('usageVariance')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('image')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {readings.map((row, index) => (
                      <tr key={row.utilityMeterId ?? row.id}>
                        <td className="px-4 py-3 text-sm">
                          {hasImportedData ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-blue-600">{row.apartmentCode}</td>
                        <td className="px-4 py-3 text-sm">{row.meterCode ?? '-'}</td>
                        <td className="px-4 py-3">
                          <input
                            readOnly={!canEditOldReading}
                            value={formatNumberDisplay(row.oldReading || '')}
                            onChange={(e) =>
                              canEditOldReading &&
                              row.utilityMeterId &&
                              handleOldReadingChange(
                                row.utilityMeterId,
                                parseNumberInput(e.target.value),
                              )
                            }
                            className={`w-24 border rounded px-2 py-1 text-sm ${
                              canEditOldReading ? '' : 'bg-gray-100'
                            }`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={formatNumberDisplay(row.newReading || '')}
                            onChange={(e) =>
                              row.utilityMeterId &&
                              handleReadingChange(row.utilityMeterId, parseNumberInput(e.target.value))
                            }
                            className="w-24 border rounded px-2 py-1 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {row.usage !== undefined ? formatNumberDisplay(row.usage) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {(() => {
                            const variance = computeUsageVariancePercent(
                              row.usage,
                              row.previousPeriodUsage,
                            );
                            return (
                              <span
                                className={
                                  isVarianceAlert(variance) ? 'text-red-600 font-medium' : 'text-gray-700'
                                }
                              >
                                {formatVarianceDisplay(variance)}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          {row.imagePath ? (
                            <button
                              type="button"
                              onClick={() =>
                                setImageModal({
                                  isOpen: true,
                                  imageUrl: row.imagePath!,
                                  fileName: row.meterCode || row.apartmentCode,
                                })
                              }
                              className="text-blue-600 text-sm"
                            >
                              📷 {t('view')}
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              row.hasExistingReading
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {row.hasExistingReading ? t('hasData') : t('noData')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!hasImportedData && (
                  <BulkListPagination
                    page={currentPage}
                    totalPages={entryTotalPages}
                    total={entryTotal}
                    onPageChange={setCurrentPage}
                    t={t}
                  />
                )}
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg disabled:opacity-50"
                  >
                    {loading ? t('processing') : t('submitMeterReadings')}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">{t('noData')}</div>
            )}
          </div>
        </div>
      )}
        </>
      )}

      {panelView === 'history' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={historyPeriodFilter}
                onChange={(e) => {
                  setHistoryPeriodFilter(e.target.value as 'selected' | 'all');
                  setHistoryPage(1);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="selected">{t('billingPeriod')}: {periodString || t('selectBillingPeriod')}</option>
                <option value="all">{t('meterReadingHistoryAllPeriods')}</option>
              </select>
              <input
                type="text"
                value={historySearch}
                onChange={(e) => {
                  setHistorySearch(e.target.value);
                  setHistoryPage(1);
                }}
                placeholder={t('searchApartmentOrMeter')}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[220px]"
              />
            </div>
            <div className="text-sm text-gray-600">
              {t('totalRecords')}: {historyTotal}
            </div>
          </div>

          <div className="p-4 overflow-x-auto">
            {loading ? (
              <div className="text-center py-12 text-gray-500">{t('loading')}</div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('exportTime')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('actions')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('apartmentCode')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('billingPeriod')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('oldReading')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('newReading')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('notes')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('cardActionStaff')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {actionLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {new Date(log.createdAt).toLocaleString('vi-VN')}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getActionBadgeClass(log.action)}`}
                          >
                            {formatActionLabel(log.action)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-blue-600">
                          {log.apartment?.code ?? '--'}
                        </td>
                        <td className="px-4 py-3 text-sm">{log.period}</td>
                        <td className="px-4 py-3 text-sm">{formatReadingChange(log, 'oldReading')}</td>
                        <td className="px-4 py-3 text-sm">{formatReadingChange(log, 'newReading')}</td>
                        <td className="px-4 py-3 text-sm">{log.notes ?? '--'}</td>
                        <td className="px-4 py-3 text-sm">
                          {log.creator?.name || log.creator?.email || '--'}
                        </td>
                      </tr>
                    ))}
                    {actionLogs.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                          {t('noData')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <BulkListPagination
                  page={historyPage}
                  totalPages={historyTotalPages}
                  total={historyTotal}
                  onPageChange={setHistoryPage}
                  t={t}
                />
              </>
            )}
          </div>
        </div>
      )}

      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ isOpen: false, imageUrl: '', fileName: '' })}
        fileName={imageModal.fileName}
        fileUrl={imageModal.imageUrl}
      />

      {panelView === 'entry' && selectedPeriod && (
        <MeterReadingImportModal
          isOpen={showImportModal}
          buildingId={buildingId}
          buildingName={buildingName}
          type={type}
          period={periodString}
          t={t}
          onClose={() => setShowImportModal(false)}
          onImported={handleImportedReadings}
        />
      )}
    </div>
  );
};
